import { createColumnHelper } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useMemo } from 'react'
import { REBATE_ENROLLMENT_COLUMNS } from './rebateEnrollmentsColumnInfo'
import { RebateEnrollment, RebateEnrollmentStatus } from '../tradespend_api'
import { formatInteger, formatUsd } from 'utils/formatting'
import { TextPill } from '../../../components/FbUI/TextPill'
import { AttributionCodeCell } from '../../../components/FbUI/StagePill'

export const useRebateEnrollmentsColumns = () => {
  const columnHelper = createColumnHelper<RebateEnrollment>()

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('tradespend_user_name', {
        header: REBATE_ENROLLMENT_COLUMNS.tradespend_user_name.header,
        size: 200,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('tradespend_user_email', {
        header: REBATE_ENROLLMENT_COLUMNS.tradespend_user_email.header,
        size: 250,
      }),
      columnHelper.accessor('company_name', {
        header: REBATE_ENROLLMENT_COLUMNS.company_name.header,
        size: 200,
      }),
      columnHelper.accessor('attribution_code', {
        header: REBATE_ENROLLMENT_COLUMNS.attribution_code.header,
        size: 200,
        cell: (info) => {
          const attributionCode = info.getValue()
          if (!attributionCode) return '-'

          // Cast to partial AttributionCode for display
          return (
            <AttributionCodeCell
              attributionCodes={[attributionCode as any]}
              limit={1}
            />
          )
        },
      }),
      columnHelper.accessor('status', {
        header: REBATE_ENROLLMENT_COLUMNS.status.header,
        size: 150,
        cell: (info) => {
          const status = info.getValue()

          // Define colors and display labels based on status
          const statusConfig: Record<
            RebateEnrollmentStatus,
            { bg: string; text: string; label: string }
          > = {
            'email captured': {
              bg: '#E0E7FF',
              text: '#3730A3',
              label: 'Email Captured',
            }, // Light indigo - anonymous subscription
            'account created': {
              bg: '#DBEAFE',
              text: '#1E3A8A',
              label: 'Account Created',
            }, // Light blue - user has account but not enrolled
            enrolled: {
              bg: '#BAE6FD',
              text: '#075985',
              label: 'Enrolled',
            }, // Sky blue - user enrolled, no claims
            'claim submitted': {
              bg: '#FEF08A',
              text: '#854D0E',
              label: 'Claim Submitted',
            }, // Light yellow - claims submitted, not approved
            'claim approved': {
              bg: '#D1FAE5',
              text: '#065F46',
              label: 'Claim Approved',
            }, // Light green - at least one claim approved
          }

          const config = statusConfig[status] || {
            bg: '#E5E7EB',
            text: '#374151',
            label: status.charAt(0).toUpperCase() + status.slice(1),
          }

          return (
            <TextPill
              backgroundColor={config.bg}
              textColor={config.text}
              style={{ marginBottom: 2 }}
            >
              {config.label}
            </TextPill>
          )
        },
      }),
      columnHelper.accessor('total_claims', {
        header: REBATE_ENROLLMENT_COLUMNS.total_claims.header,
        size: 120,
        cell: (info) => formatInteger(info.getValue()),
      }),
      columnHelper.accessor('pending_validation_claims', {
        header: REBATE_ENROLLMENT_COLUMNS.pending_validation_claims.header,
        size: 120,
        cell: (info) => formatInteger(info.getValue()),
      }),
      columnHelper.accessor('invalid_claims', {
        header: REBATE_ENROLLMENT_COLUMNS.invalid_claims.header,
        size: 120,
        cell: (info) => formatInteger(info.getValue()),
      }),
      columnHelper.accessor('unpaid_valid_claims', {
        header: REBATE_ENROLLMENT_COLUMNS.unpaid_valid_claims.header,
        size: 120,
        cell: (info) => formatInteger(info.getValue()),
      }),
      columnHelper.accessor('paid_claims', {
        header: REBATE_ENROLLMENT_COLUMNS.paid_claims.header,
        size: 120,
        cell: (info) => formatInteger(info.getValue()),
      }),
      columnHelper.accessor('total_validated_units', {
        header: REBATE_ENROLLMENT_COLUMNS.total_validated_units.header,
        size: 120,
        cell: (info) => {
          const units = info.getValue()
          return units !== null ? units.toFixed(2) : '-'
        },
      }),
      columnHelper.accessor('total_validated_payout', {
        header: REBATE_ENROLLMENT_COLUMNS.total_validated_payout.header,
        size: 120,
        cell: (info) => {
          const amount = info.getValue()
          return amount !== null ? formatUsd(amount) : '-'
        },
      }),
      columnHelper.accessor('created', {
        header: REBATE_ENROLLMENT_COLUMNS.created.header,
        size: 150,
        cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy'),
      }),
    ]
  }, [])

  return columns
}
